import { useRef, useState, useEffect } from 'react'
import { TextField, Button, Typography, Stack, Grid, IconButton } from '@mui/material'
import { AddCircleRounded } from '@mui/icons-material'
import { SmartDialog } from '../utilities/SmartDialog'
import { apiPut, apiGet } from '../Api_Functions'

import MenuItem from '@mui/material/MenuItem'
import { useStateSetNewValueOnChangeToTrue } from '../hooks/useStateSetNewValueOnChangeToTrue'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartSelect from '../smartSelect/SmartSelect'
import { PART_RESULT_OK, PART_RESULT_NOK, partResultLabel } from '../utilities/partResultLabel'

// RN1/INV2/D16: the catalogue this dialog offers is the same 7 values Sun publishes, none split
// in two. The FINISHED_OK/FINISHED_NOK translation layer this dialog used to share with editing a
// row (generic/utilities/partFinishedStatus) is retired entirely: Part.jsx (S22-S24) now owns its
// own small status/result editor instead.
const FINISHED_STATUS = 'FINISHED'
const RESULT_OPTIONS = [PART_RESULT_OK, PART_RESULT_NOK]
const DEFAULT_RESULT = PART_RESULT_OK

const AddElement = (props) => {
  const { t_ } = useSmartTranslation()
  const {
    endpoint,
    partReference,
    title,
    referenceName,
    disable,
    onAddDone,
    onAlert
  } = props

  const [showDialog, setShowDialog] = useState(false)
  const [rawMaterialReferenceList, setRawMaterialReferenceList] = useState([])
  const quantity = useRef(1)
  const [rawMaterialReference, setRawMaterialReference] = useStateSetNewValueOnChangeToTrue(partReference && partReference.rawMaterialReference ? partReference.rawMaterialReference.id : null, showDialog)
  const [partStatuses, setPartStatuses] = useState([])
  const [status, setStatus] = useState(null)
  // RN2/RN4: the result only exists (and is meaningful) while the chosen status is FINISHED.
  // P1/D9-bis: preselected in OK, so V5's validation stays defensive, never a normal path.
  const [result, setResult] = useState('')

  useEffect(() => {
    apiGet('partStatuses').then(data => {
      const statuses = data || []
      setPartStatuses(statuses)
      const firstStatus = statuses.length > 0 ? statuses[0] : null
      setStatus(firstStatus)
      setResult(firstStatus === FINISHED_STATUS ? DEFAULT_RESULT : '')
    })
  }, [])

  // RN12: the result is never derived from the status, nor the other way round. Choosing FINISHED
  // preselects OK (P1); leaving FINISHED retires the result instead of leaving it stuck (S14).
  const handleStatusChange = (newStatus) => {
    setStatus(newStatus)
    setResult(newStatus === FINISHED_STATUS ? DEFAULT_RESULT : '')
  }

  const isFinished = status === FINISHED_STATUS
  // V5/E3: defensive validation. With the preselection in OK this is only reachable by a manual
  // clear of the field or a catalogue load failure, never by the normal flow (S15).
  const resultMissing = isFinished && (result === null || result === '')

  const updateQuantity = () => {
    if (!endpoint) return

    if (quantity.current <= 0) {
      return
    }
    if (rawMaterialReference === null || rawMaterialReference === '') {
      return
    }
    if (status === null || status === '' || status === undefined) {
      return
    }
    if (resultMissing) {
      // S15: the request is never sent; the field stays in error until it is fixed.
      return
    }
    setShowDialog(false)

    let url = endpoint + '/' + partReference.id + '/' + quantity.current + '/' + rawMaterialReference + '/' + status
    if (isFinished) {
      url += '/' + (result === PART_RESULT_OK)
    }
    apiPut(url)
      .catch(err => {
        onAlert({
          title: t_('Error updating') + ' ',
          severity: 'error',
          message: t_('Could not be updated:') + ' ' + err
        })
      }).then(() => { if (onAddDone) { onAddDone() } })
    quantity.current = 1
  }

  const onClickFunction = async () => {
    const rawMaterialReferences = await apiGet('rawMaterialsReferences/' + partReference.id)
    setRawMaterialReferenceList(rawMaterialReferences)
    setShowDialog(true)
  }

  return (
    <div>
      <SmartDialog
        setOpen={showDialog}
        minWidth='40rem'
        acceptCallback={() => {
          updateQuantity()
        }}
        cancelCallback={() => setShowDialog(false)}
        title={
          {
            visible: true,
            icon: <AddCircleRounded color='primary' sx={{ fontSize: '1.5em' }} />,
            render:
  <Stack
    direction='row'
    justifyContent='center'
    alignItems='center'
  >
    <Typography variant='h6' color='primary'> {referenceName}</Typography>
  </Stack>
          }
        }
        renderComponent={
          <>
            <Grid container spacing={3} justifyContent='center' sx={{ padding: (theme) => theme.spacing(2) }}>
              <Grid item xs={2}>
                <TextField
                  label={t_('Quantity')} defaultValue={1} type='number' onChange={(event) => { quantity.current = event.target.value }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  value={rawMaterialReference || ''}
                  error={rawMaterialReference === null || rawMaterialReference === ''}
                  label={t_('Raw Material Reference')}
                  onChange={(event) => { setRawMaterialReference(event.target.value) }}
                >
                  {rawMaterialReferenceList.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.code}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <SmartSelect
                  value={status}
                  selectableOptions={partStatuses}
                  onChange={handleStatusChange}
                  label={t_('Status')}
                />
              </Grid>
              {isFinished && (
                <Grid item xs={4}>
                  <SmartSelect
                    value={result}
                    selectableOptions={RESULT_OPTIONS}
                    optionLabelTransform={(option) => partResultLabel(option, t_)}
                    onChange={setResult}
                    label={t_('Result')}
                    error={resultMissing}
                    helperText={resultMissing ? t_('Result is required') : ''}
                    disableClearable={false}
                  />
                </Grid>
              )}
            </Grid>
          </>

        }
      />
      {title === undefined
        ? (
          <IconButton
            color='primary'
            onClick={onClickFunction}
            disabled={disable}
          >
            <AddCircleRounded />
          </IconButton>)
        : (
          <Button
            color='primary'
            onClick={onClickFunction}
            disabled={disable}
          >
            <AddCircleRounded style={{ fontSize: '1.2em' }} />
            <Typography style={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{title}</Typography>
          </Button>)}
    </div>
  )
}

export default AddElement
